import React from 'react'
import { GOLD } from '../../utils/luckyDraw'
import {
  CARD_BORDER_PX,
  CARD_GAP_PX,
  CARD_PADDING_PX,
  INSPECT_CLASS,
  LIT_CLASS,
  NAME_LINES,
  NAME_LINE_HEIGHT,
  WON_CLASS,
  cardFontCss,
  mosaicQueries,
} from './draw'

const CARD_CLASS = 'card'
const NAME_CLASS = 'card-name'

/**
 * Every card rule, spread into the grid's sx. Cards are plain divs: 700 MUI `sx` props cost 61 ms
 * a rebuild to produce two classes, against 0.1 ms for this whole sheet.
 *
 * Declaration order is emission order — tints after the base, lit after won so a card that is both
 * reads gold, hover last so it lifts a card in any state.
 */
export const cardRules = (columns, rows) => {
  const [tooNarrow, tooShort] = mosaicQueries(columns, rows)

  return {
    // The grid is the size container, so cards inherit one type size rather than each rounding
    // its own fractional box.
    containerType: 'size',
    fontSize: cardFontCss(columns, rows),
    gap: `${CARD_GAP_PX}px`,

    [`& .${CARD_CLASS}`]: {
      // From draw.js, so the box the type is sized against is the box that renders.
      px: `${CARD_PADDING_PX}px`,
      border: `${CARD_BORDER_PX}px solid`,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      // A card is under 20px tall at a big wall, so 8px would round it to a pill.
      borderRadius: '4px',
      // Repaints stay inside the card, so lighting one does not invalidate the wall around it.
      contain: 'paint',
      color: '#fff',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.55)',
    },

    // Alternating, so one dealer's tickets read as a block. Cool only: gold means a winner. Flat,
    // because a gradient is a paint pass per card and a transition a layer per card.
    [`& .${CARD_CLASS}-0`]: {
      backgroundColor: 'rgb(56,104,170)',
      borderColor: 'rgba(150,198,255,0.75)',
    },
    [`& .${CARD_CLASS}-1`]: {
      backgroundColor: 'rgb(110,70,150)',
      borderColor: 'rgba(205,160,240,0.75)',
    },

    // Spent. Only the drawn card greys, so the wall says nothing about who is still eligible.
    // Colour only: re-sizing the name would be a text relayout.
    [`& .${CARD_CLASS}.${WON_CLASS}`]: {
      backgroundColor: '#4a4a52',
      borderColor: 'rgba(255, 255, 255, 0.22)',
      color: 'rgba(255, 255, 255, 0.78)',
      textShadow: 'none',
    },

    // Lit. Same size and weight as every other card — either would be a relayout twice a flash.
    // zIndex only so the bloom covers its neighbours.
    [`& .${CARD_CLASS}.${LIT_CLASS}`]: {
      background: `linear-gradient(180deg, #ffe89a 0%, ${GOLD} 100%)`,
      borderColor: '#fff3c4',
      // Ring stays 1px against the card's 1px border; at 2px they read as one heavy band.
      boxShadow: '0 0 24px 8px rgba(247,201,72,0.5), inset 0 0 0 1px rgba(255,255,255,0.9)',
      color: '#101010',
      textShadow: 'none',
      zIndex: 3,
    },

    // Lifted so its name reads whatever the wall's size.
    [`&.${INSPECT_CLASS} .${CARD_CLASS}:hover`]: {
      transform: 'scale(1.6)',
      zIndex: 4,
      borderColor: 'rgba(255, 255, 255, 0.65)',
      cursor: 'default',
    },

    [`& .${NAME_CLASS}`]: {
      // Clamped to the same NAME_LINES the font size is budgeted for. The card's overflow would
      // hide a longer name anyway, but centred, so it would slice the caps top and bottom.
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: NAME_LINES,
      overflow: 'hidden',
      lineHeight: NAME_LINE_HEIGHT,
      textAlign: 'center',
      textTransform: 'uppercase',
      overflowWrap: 'anywhere',
      // Dropped once the wall is too small to render it legibly: the mosaic.
      [`@container ${tooNarrow}`]: { display: 'none' },
      [`@container ${tooShort}`]: { display: 'none' },
    },
  }
}

/** One ticket. Plain divs, not Box: the styling is on the grid, and this renders 700 times. */
const TicketCard = ({ card, index, onNode }) => (
  <div
    className={`${CARD_CLASS} ${CARD_CLASS}-${card.tint}`}
    // What the hover handler's closest() finds, so the peek needs no listener per card. The
    // winners' popout finds the card its rows fly from by it too.
    data-ticket={card.ticket}
    ref={(node) => onNode(index, node)}
  >
    <div className={NAME_CLASS}>{card.companyName}</div>
  </div>
)

export default TicketCard
